/**
 * Interface sound layer:
 *  - `installAudio()`: one-time app-wide setup (engine + mixer listeners, hub ambience, and a
 *    delegated listener that gives every button / tab / link a click and, outside a run, a very
 *    quiet hover tick). Elements opt out or pick a sound with `data-sfx="none" | "<sound id>"`.
 *  - `playUiCue(id, notes)`: the meta `keystone:sfx` cues (loot, forge, offering) — sampled
 *    layers (bank.ts UI_CUE_LAYERS) plus the cue's chime notes, played on the sampled steel chime
 *    (`ui.chime`, derived from TomMusic's sword ring) pitched to each note; the old sine synth is
 *    only a fallback while the UI sprite is not decoded.
 * Never throws.
 */
import { ambience } from './ambience';
import { uiCueLayers } from './bank';
import { combatAudio } from './engine';
import { mixer } from './mixer';
import { samples } from './samples';

/** A synthesized chime note (same shape as meta/audio/sfx.ts). */
export interface UiNote {
  f: number;
  t?: number;
  d?: number;
  g?: number;
  w?: OscillatorType;
  to?: number;
}

/** Keeps the (fallback) synth chimes at their old level relative to the UI bus. */
const NOTE_TRIM = 0.38;
/** Pitch of the sampled `ui.chime` clip (C6), Hz. */
export const CHIME_HZ = 1046.5;
/** The voice limiter merges one id retriggered within 30 ms: chord notes are strummed this far apart. */
const STRUM_S = 0.035;
const CLICKABLE = 'button, [role="button"], [role="tab"], [role="menuitem"], a[href], summary, select, input[type="checkbox"], input[type="radio"], [data-sfx]';
/** In-run containers whose clicks already have their own feedback (abilities cast, etc.). */
const SILENT_IN = '.actionbar, canvas';

let installed = false;
const cueAt = new Map<string, number>();
/** Same cue id within this window plays once (a burst of pickups is one chime). */
const CUE_DEBOUNCE_S = 0.055;
let lastHover: Element | null = null;

/** Which interface sound an element's click plays (null = none). Pure apart from DOM reads. */
export function clickSoundFor(target: Element | null): string | null {
  if (!target || typeof target.closest !== 'function') return null;
  const tagged = target.closest('[data-sfx]');
  if (tagged) {
    const v = tagged.getAttribute('data-sfx') ?? '';
    return v === 'none' || v === '' ? null : v;
  }
  const el = target.closest(CLICKABLE);
  if (!el || el.closest(SILENT_IN)) return null;
  if (el.getAttribute('aria-disabled') === 'true' || (el as HTMLButtonElement).disabled) return null;
  if (el.getAttribute('role') === 'tab' || /\btab\b|-tab\b|tabs?-/.test(el.className?.toString?.() ?? '')) return 'ui.tab';
  const label = `${el.getAttribute('aria-label') ?? ''} ${el.className?.toString?.() ?? ''}`.toLowerCase();
  if (/\bclose\b|dismiss|-close|close-/.test(label)) return 'ui.close';
  return 'ui.click';
}

/** One-time app-wide audio setup (idempotent). */
export function installAudio(): void {
  combatAudio.install();
  if (installed || typeof document === 'undefined') return;
  installed = true;
  ambience.setHub();
  if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) Object.assign(globalThis as object, { __audio: { engine: combatAudio, ambience, mixer } });
  document.addEventListener(
    'click',
    (e) => {
      try {
        const id = clickSoundFor(e.target as Element | null);
        if (id) combatAudio.play(id);
      } catch {
        /* ignore */
      }
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    'pointerover',
    (e) => {
      try {
        if ((e as PointerEvent).pointerType !== 'mouse' || ambience.inRun) return;
        const t = e.target as Element | null;
        const el = t?.closest?.(CLICKABLE) ?? null;
        if (el === lastHover) return;
        lastHover = el;
        if (el && clickSoundFor(el) && !el.closest('[data-sfx="none"]')) combatAudio.play('ui.hover');
      } catch {
        /* ignore */
      }
    },
    { capture: true, passive: true },
  );
}

/**
 * Play a meta UI cue: sampled layers for it plus its chime `notes`. Returns true when the shared
 * mixer handled it (played, or muted by the sound settings), false when audio isn't up yet.
 */
export function playUiCue(id: string, notes: readonly UiNote[] | null): boolean {
  try {
    combatAudio.install();
    if (!mixer.enabled) return true;
    const ctx = mixer.ctx;
    if (!ctx) return false;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const now = ctx.currentTime;
    if (now - (cueAt.get(id) ?? -1) < CUE_DEBOUNCE_S) return true;
    cueAt.set(id, now);
    for (const s of uiCueLayers(id)) combatAudio.play(s);
    if (notes?.length && mixer.level('ui') > 0) {
      if (samples.clip('ui.chime#0')) playChimes(notes);
      else playNotes(ctx, notes);
    }
    return true;
  } catch {
    return false;
  }
}

/** Sampled-chime timing for a cue's notes: (pitch ratio, delay, gain) per note. Pure. */
export function chimePlan(notes: readonly UiNote[]): { pitch: number; delay: number; gain: number }[] {
  const out: { pitch: number; delay: number; gain: number }[] = [];
  const sorted = [...notes].sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
  let last = -1;
  for (const n of sorted) {
    const delay = Math.max(n.t ?? 0, last + STRUM_S);
    last = delay;
    // Sine-synth gains were ~0.3–0.6 around a 0.4 design level.
    out.push({ pitch: Math.max(0.25, Math.min(4, n.f / CHIME_HZ)), delay, gain: Math.max(0.15, Math.min(1.4, (n.g ?? 0.4) / 0.45)) });
  }
  return out;
}

function playChimes(notes: readonly UiNote[]): void {
  for (const p of chimePlan(notes)) combatAudio.play('ui.chime', { pitch: p.pitch, delay: p.delay, gain: p.gain });
}

function playNotes(ctx: AudioContext, notes: readonly UiNote[]): void {
  const bus = mixer.input('ui');
  if (!bus) return;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 5200;
  const out = ctx.createGain();
  out.gain.value = NOTE_TRIM;
  lp.connect(out).connect(bus);
  const t0 = ctx.currentTime + 0.005;
  let end = t0;
  for (const n of notes) {
    const start = t0 + (n.t ?? 0);
    const dur = n.d ?? 0.15;
    end = Math.max(end, start + dur);
    const osc = ctx.createOscillator();
    osc.type = n.w ?? 'sine';
    osc.frequency.setValueAtTime(n.f, start);
    if (n.to) osc.frequency.exponentialRampToValueAtTime(n.to, start + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, n.g ?? 0.4), start + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(g).connect(lp);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  }
  setTimeout(() => {
    try {
      out.disconnect();
    } catch {
      /* gone */
    }
  }, (end - ctx.currentTime + 0.5) * 1000);
}
